import axios from 'axios';
import { apaleoConfig } from '../config/apaleo.config';
import { apiConfig } from '../config/api.config';

/**
 * Apaleo OAuth Utilities
 */

// Token storage keys
export const TOKEN_KEY = 'apaleo_access_token';
export const REFRESH_TOKEN_KEY = 'apaleo_refresh_token';
export const TOKEN_EXPIRY_KEY = 'apaleo_token_expiry';
export const STATE_KEY = 'apaleo_oauth_state';

/**
 * Generate random state for CSRF protection
 */
export const generateState = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join(
    ''
  );
};

/**
 * Initiate OAuth flow - redirect to Apaleo authorization
 */
export const initiateApaleoAuth = (): void => {
  sessionStorage.removeItem(STATE_KEY);

  const state = generateState();
  sessionStorage.setItem(STATE_KEY, state);
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: apaleoConfig.clientId,
    redirect_uri: apaleoConfig.redirectUri,
    scope: apaleoConfig.scopes.join(' '),
    state,
  });

  const authUrl = `${apaleoConfig.authorizationEndpoint}?${params.toString()}`;
  window.location.href = authUrl;
};

/**
 * Exchange authorization code for access token
 */
// This function is now handled by RTK Query in apaleoSlice.ts
// Use useExchangeCodeForTokenMutation from apaleoSlice instead

/**
 * Get stored access token
 */
export const getAccessToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  const token = getAccessToken();
  const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);

  console.log('Auth check - Token exists:', !!token);
  console.log('Auth check - Expiry exists:', !!expiry);

  if (!token || !expiry) {
    console.log('Auth check failed: Missing token or expiry');
    return false;
  }

  const isTokenValid = Date.now() < parseInt(expiry, 10);
  console.log('Auth check - Token valid:', isTokenValid);

  if (!isTokenValid) {
    console.log('Auth check failed: Token expired');
  }

  return isTokenValid;
};

/**
 * Refresh access token
 */
export const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  try {
    // Call backend API for token refresh
    const response = await axios.post(
      apiConfig.endpoints.tokenRefresh,
      {
        refresh_token: refreshToken,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const { access_token, refresh_token, expires_in } = response.data;

    localStorage.setItem(TOKEN_KEY, access_token);
    localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
    localStorage.setItem(
      TOKEN_EXPIRY_KEY,
      (Date.now() + expires_in * 1000).toString()
    );

    return access_token;
  } catch (error: any) {
    console.error(
      'Token refresh error:',
      error.response?.data || error.message
    );
    clearTokens();
    throw new Error('Failed to refresh token');
  }
};

/**
 * Get valid token (refresh if expired)
 */
export const getValidToken = async (): Promise<string> => {
  let token = getAccessToken();
  const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);

  if (!token || !expiry || Date.now() >= parseInt(expiry, 10)) {
    token = await refreshAccessToken();
  }

  return token;
};

/**
 * Clear all tokens (logout)
 */
export const clearTokens = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
  sessionStorage.removeItem(STATE_KEY);
};

/**
 * Logout user
 */
export const logout = (): void => {
  clearTokens();
};
